import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "./api/axios";
import { useAuth } from "./contexts/AuthContext";
import { getImageUrl } from "./utils/image";
import { useActivityLogger } from "./hooks/useActivity";

export default function EditProfile() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { user, fetchUser } = useAuth();

  const [profilePic, setProfilePic] = useState("/avatar.png");
  const [profilePicFile, setProfilePicFile] = useState(null);
  const [nik, setNik] = useState("");
  const [alias, setAlias] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [preferences, setPreferences] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  
  // State untuk Pasien (Khusus Pendamping)
  const [myPatients, setMyPatients] = useState([]); // Pasien yang sudah terhubung
  const [allPatients, setAllPatients] = useState([]); // Master data semua pasien untuk search
  const [searchQuery, setSearchQuery] = useState(""); // Keyword pencarian
  const [searchResults, setSearchResults] = useState([]); // Hasil filter pencarian
  const [selectedPatientId, setSelectedPatientId] = useState(null); // ID yang dipilih dari search
  const [addingPatient, setAddingPatient] = useState(false);

  const { logUserActivity } = useActivityLogger();

  const allPreferences = [
    "Informasi Medis",
    "Kesehatan Mental",
    "Keagamaan Islam",
    "Keagamaan Kristen",
    "Keagamaan Katolik",
    "Keagamaan Hindu",
    "Keagamaan Buddha",
    "Keagamaan Konghucu",
    "Kepercayaan Lainnya",
  ];

  const toRoleId = (r) => Number(r?.ID ?? r?.id ?? r?.role_id ?? r?.RoleID);

  // Helper untuk cek role pendamping
  const isPendamping = user?.roles?.some((r) => {
    const roleName = r.name || r.Name || "";
    return roleName.toLowerCase() === "pendamping";
  });

  // Fetch available roles
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await api.get("/roles");
        const editable = (response.data || []).filter((r) => r.is_editable);
        setAvailableRoles(editable);
      } catch (error) {
        console.error("Error fetching roles:", error);
        setErrorMessage("Gagal memuat data roles");
      }
    };
    fetchRoles();
  }, []);

  // Fetch Data Pasien (List Pasien Saya & Master Data Pasien untuk Search)
  const fetchPatientData = async () => {
    if (!isPendamping) return;
    try {
      // 1. Ambil pasien yang sudah terhubung
      const responseMy = await api.get("/caregiver/patients");
      setMyPatients(responseMy.data || []);

      // 2. Ambil semua pasien untuk fitur search
      // Note: Pastikan backend endpoint /patients tersedia dan mengembalikan semua user dengan role 'patient'
    } catch (error) {
      console.error("Error fetching patients data:", error);
    }

    try {
      const responseAll = await api.get("/patients");
      setAllPatients(responseAll.data || []);
      console.log("Fetched all patients for search:", responseAll.data);
    }
    catch (error) {
      console.error("Error fetching all patients:", error);
    }
  };
  const handleRemovePatient = async (patientId) => {
    if (!window.confirm("Apakah Anda yakin ingin menghapus pasien ini dari daftar Anda?")) {
      return;
    }

    setLoading(true);
    try {
      await api.delete(`/caregiver/patients/${patientId}`);
      
      // Refresh list
      await fetchPatientData();
      
      logUserActivity(
        "user",
        "remove_patient",
        "EditProfile",
        `Caregiver removed patient ID: ${patientId}`
      );
    } catch (error) {
      console.error("Error removing patient:", error);
      setErrorMessage(
        error.response?.data?.error || "Gagal menghapus pasien."
      );
    } finally {
      setLoading(false);
    }
  };
  

  useEffect(() => {
    if (user) {
      setProfilePic(getImageUrl(user.image_url) || "/avatar.png");
      setNik(user.nik || "");
      setAlias(user.nama_samaran || "");
      setPhone(user.nomor_hp || "");
      setEmail(user.email || "");
      setPreferences(user.preferences || []);

      if (Array.isArray(user.roles)) {
        const roleIds = user.roles
          .map(toRoleId)
          .filter((v) => Number.isFinite(v));
        setSelectedRoles(roleIds);
      } else {
        setSelectedRoles([]);
      }

      logUserActivity(
        "user",
        "view_edit_profile",
        "EditProfile",
        "User viewed edit profile page"
      );
    }
  }, [user]);

  // Trigger fetch patients ketika status pendamping terkonfirmasi
  useEffect(() => {
    if (isPendamping) {
      fetchPatientData();
    }
  }, [isPendamping]);

  // Logic Pencarian (Filter Client-Side)
  useEffect(() => {
    if (!searchQuery || searchQuery.trim() === "") {
      setSearchResults([]);
      return;
    }

    const lowerQuery = searchQuery.toLowerCase();
    
    // Filter dari allPatients
    const results = allPatients.filter((p) => {
      // Jangan tampilkan pasien yang sudah menjadi pasien saya
      const isAlreadyMyPatient = myPatients.some(mp => mp.ID === p.ID);
      if (isAlreadyMyPatient) return false;

      // Jangan tampilkan diri sendiri (jika case aneh terjadi)
      if (p.ID === user?.ID) return false;

      return (
        (p.nama_ktp && p.nama_ktp.toLowerCase().includes(lowerQuery)) ||
        (p.username && p.username.toLowerCase().includes(lowerQuery)) ||
        (p.email && p.email.toLowerCase().includes(lowerQuery))
      );
    });

    setSearchResults(results);
  }, [searchQuery, allPatients, myPatients, user]);

  const handleSelectPatient = (patient) => {
    setSelectedPatientId(patient.ID);
    setSearchQuery(`${patient.nama_ktp} (@${patient.username})`);
    setSearchResults([]); // Sembunyikan dropdown
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrorMessage("Ukuran file gambar maksimal 5MB");
        return;
      }
      if (!file.type.startsWith("image/")) {
        setErrorMessage("File harus berupa gambar");
        return;
      }
      setProfilePic(URL.createObjectURL(file));
      setProfilePicFile(file);
      setErrorMessage("");
    }
  };

  const togglePreference = (pref) => {
    if (preferences.includes(pref)) {
      setPreferences(preferences.filter((p) => p !== pref));
    } else {
      setPreferences([...preferences, pref]);
    }
  };

  const toggleRole = (roleId) => {
    const idNum = Number(roleId);
    if (!Number.isFinite(idNum)) return;
    setSelectedRoles((prev) => {
      const set = new Set(prev.map(Number));
      if (set.has(idNum)) {
        set.delete(idNum);
      } else {
        set.add(idNum);
      }
      return Array.from(set);
    });
  };

  const handleAddPatient = async () => {
    if (!selectedPatientId) return;
    setAddingPatient(true);
    setErrorMessage("");
    
    try {
      await api.post("/caregiver/patients", {
        patient_id: parseInt(selectedPatientId),
      });
      
      await fetchPatientData(); // Refresh list
      setSearchQuery("");
      setSelectedPatientId(null);
      setSearchResults([]);
      
      logUserActivity(
        "user",
        "add_patient",
        "EditProfile",
        `Caregiver added patient ID: ${selectedPatientId}`
      );
    } catch (error) {
      console.error("Error adding patient:", error);
      setErrorMessage(
        error.response?.data?.error || "Gagal menambahkan pasien."
      );
    } finally {
      setAddingPatient(false);
    }
  };

  const validateForm = () => {
    setErrorMessage("");
    if (nik.trim() && nik.length !== 16) {
      setErrorMessage("NIK harus 16 digit");
      return false;
    }
    if (email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setErrorMessage("Format email tidak valid");
        return false;
      }
    }
    if (phone.trim()) {
      const phoneRegex = /^(\+62|62|0)[0-9]{9,13}$/;
      if (!phoneRegex.test(phone)) {
        setErrorMessage("Format nomor HP tidak valid");
        return false;
      }
    }
    if (password.trim() && password.length < 6) {
      setErrorMessage("Password minimal 6 karakter");
      return false;
    }
    if (password.trim() && password !== confirmPassword) {
      setErrorMessage("Konfirmasi password tidak cocok");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setErrorMessage("");

    const formData = new FormData();
    if (nik.trim()) formData.append("nik", nik);
    if (alias.trim()) formData.append("nama_samaran", alias);
    if (phone.trim()) formData.append("nomor_hp", phone);
    if (email.trim()) formData.append("email", email);
    if (password.trim()) formData.append("password", password);
    if (preferences.length > 0) {
      formData.append("preferences", preferences.join(","));
    }
    if (selectedRoles.length > 0) {
      formData.append("role_ids", selectedRoles.join(","));
    } else {
      formData.append("role_ids", "");
    }
    if (profilePicFile) {
      formData.append("image", profilePicFile);
    }

    try {
      await api.put("/profile", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await fetchUser();
      logUserActivity(
        "user",
        "save_profile",
        "EditProfile",
        "User saved profile"
      );
      navigate("/profile");
    } catch (error) {
      console.error("Error saving profile:", error);
      const errorMsg = error.response?.data?.error || "Gagal menyimpan profil.";
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white px-4 py-4 pb-32">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => {
            logUserActivity("navigation", "go_back", "EditProfile", "Back click");
            navigate("/profile");
          }}
        >
          <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
        </button>
        <h1 className="text-lg font-semibold">Edit Profil</h1>
        <div className="w-6" />
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{errorMessage}</p>
        </div>
      )}

      {/* Profile Picture */}
      <div className="flex flex-col items-center mb-6">
        <div className="relative">
          <img
            src={profilePic}
            alt="Profile"
            className="h-24 w-24 rounded-full object-cover"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current.click()}
            className="absolute bottom-0 right-0 bg-blue-300 p-2 rounded-full"
            disabled={loading}
          >
            <img src="/icon-edit.png" alt="Edit" className="h-3 w-3" />
          </button>
        </div>
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          onChange={handleImageChange}
          className="hidden"
        />
        <p className="text-xs text-gray-500 mt-2">Maksimal 5MB, format: JPG, PNG</p>
      </div>

      {/* Profile Form */}
      <form className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-1 block">
            NIK <span className="text-gray-500">(16 digit)</span>
          </label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            value={nik}
            onChange={(e) => setNik(e.target.value)}
            maxLength="16"
            disabled={loading}
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-1 block">Nama Samaran</label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
            disabled={loading}
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-1 block">No. HP</label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={loading}
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-1 block">E-mail</label>
          <input
            type="email"
            className="w-full border rounded-lg px-3 py-2"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-1 block">Password Baru</label>
          <input
            type="password"
            className="w-full border rounded-lg px-3 py-2"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Kosongkan jika tidak ubah"
            disabled={loading}
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-1 block">Ulangi Password Baru</label>
          <input
            type="password"
            className="w-full border rounded-lg px-3 py-2"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Preferensi</label>
          <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-3">
            {allPreferences.map((pref) => (
              <label key={pref} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.includes(pref)}
                  onChange={() => togglePreference(pref)}
                  className="form-checkbox h-4 w-4 text-blue-600 rounded"
                  disabled={loading}
                />
                <span className="text-sm">{pref}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Roles</label>
          <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-3">
            {availableRoles.map((role) => {
              const idNum = Number(role?.ID ?? role?.id);
              if (!Number.isFinite(idNum)) return null;
              return (
                <label key={idNum} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedRoles.map(Number).includes(idNum)}
                    onChange={() => toggleRole(idNum)}
                    className="form-checkbox h-4 w-4 text-blue-600 rounded"
                    disabled={loading}
                  />
                  <span className="text-sm">{role.name}</span>
                </label>
              );
            })}
          </div>
        </div>

        {/* SECTION KELOLA PASIEN (KHUSUS PENDAMPING) */}
        {isPendamping && (
          <div className="mt-6 border-t pt-4">
            <h3 className="text-md font-bold mb-3 text-blue-800">Kelola Pasien</h3>
            
            <div className="mb-4 relative">
              <label className="text-sm font-medium mb-1 block">Cari & Tambah Pasien</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type="text"
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="Cari nama, username, atau email..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setSelectedPatientId(null); // Reset selection jika mengetik ulang
                    }}
                    disabled={addingPatient || loading}
                  />
                  
                  {/* Dropdown Hasil Pencarian */}
                  {searchResults.length > 0 && (
                    <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                      {searchResults.map((p) => (
                        <div
                          key={p.ID}
                          className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                          onClick={() => handleSelectPatient(p)}
                        >
                          <p className="text-sm font-medium text-gray-800">{p.nama_ktp}</p>
                          <p className="text-xs text-gray-500">@{p.username} - {p.email}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {searchQuery && searchResults.length === 0 && !selectedPatientId && (
                     <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 p-2 text-center text-xs text-gray-500">
                       Tidak ditemukan
                     </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={handleAddPatient}
                  disabled={addingPatient || !selectedPatientId || loading}
                  className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 disabled:opacity-50 text-sm font-medium whitespace-nowrap"
                >
                  {addingPatient ? "..." : "Tambah"}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                *Ketik nama pasien untuk mencari.
              </p>
            </div>

            <div className="mb-2">
              <label className="text-sm font-medium mb-2 block">Daftar Pasien Anda:</label>
              {myPatients.length === 0 ? (
                <p className="text-sm text-gray-500 italic">Belum ada pasien terhubung.</p>
              ) : (
                <ul className="border rounded-lg divide-y bg-gray-50">
                  {myPatients.map((p) => (
                    <li key={p.ID} className="p-3 text-sm flex justify-between items-center">
                      <div>
                        <span className="font-semibold block text-gray-900">{p.nama_ktp}</span>
                        <span className="text-xs text-gray-500">@{p.username}</span>
                      </div>
                      
                      {/* Tombol Hapus */}
                      <button
                        type="button"
                        onClick={() => handleRemovePatient(p.ID)}
                        disabled={loading}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-full transition-colors"
                        title="Hapus Pasien"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </form>

      {/* Sticky Save Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white px-6 pb-6 pt-4 shadow-[0_-2px_4px_rgba(0,0,0,0.1)]">
        <button
          onClick={() => setShowConfirmModal(true)}
          className="w-full bg-blue-500 text-white py-2 rounded-md hover:bg-blue-600 transition disabled:opacity-50"
          disabled={loading}
        >
          {loading ? "Menyimpan..." : "Simpan Perubahan"}
        </button>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full text-center mx-4 shadow-lg">
            <h3 className="text-lg font-semibold mb-2">Simpan Perubahan?</h3>
            <p className="text-sm text-gray-700 mb-6">
              Apakah Anda yakin ingin menyimpan perubahan profil?
            </p>
            <div className="flex justify-around text-sm font-medium">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="text-gray-500 hover:underline"
                disabled={loading}
              >
                Batal
              </button>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  handleSave();
                }}
                className="text-blue-600 hover:underline"
                disabled={loading}
              >
                Simpan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}